import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import HttpService from '../../services/HttpService';
import UserService from '../../services/UserService';
import AbonnementCollapsibleKlant from './Components/AbonnementCollapsibleKlantDashboardNew';
import NieuweInterfacesTabel from './Components/NieuweInterfacesTabel';
import BerichtenGraph from './Components/BerichtenGraph';
import BerichtDownloadComponent from './Components/BerichtDownloadComponent';
import KlantDetailsSection from '../Klant/Components/KlantDetailsSection';
import Months from '../../Enum/Months';
import '../../css/Pages.css';

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Hook for fetching customer data
 */
const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      const debiNr = UserService.getDebiNr();
      const erp = UserService.getERP();
      const axios = HttpService.getAxiosClient();
      
      // Return early only if neither parameter is present
      if ((!debiNr || debiNr.length === 0) && (!erp || erp.length === 0)) return;

      // Build query parameters
      const params = new URLSearchParams();

      if (debiNr && debiNr.length > 0) {
        debiNr.forEach(n => params.append('debiNr', n));
      }

      if (erp && erp.length > 0) {
        params.append('erp', erp);
      }

      const url = `${process.env.REACT_APP_API_URL}/klanten?${params.toString()}`;
      
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(url);
        const sortedCustomers = response.data.sort((a, b) => 
          a.naam.localeCompare(b.naam)
        );
        setCustomers(sortedCustomers);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  return { customers, loading, error };
};

/**
 * Hook for fetching detailed customer statistics
 */
const useCustomerStatistics = (customer, axios) => {
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!customer || !axios || customer.abonnementen?.length > 0) return;

      setLoading(true);
      
      try {
        const url = `${process.env.REACT_APP_API_URL}/Statistieken/Klant?debiNr=${customer.debiNr}`;
        const response = await axios.get(url);
        setStatistics(response.data[0]);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [customer?.debiNr, axios]);

  return { statistics, loading };
};

/**
 * Hook for managing period filters (year/month)
 */
const usePeriodFilters = (berichtenPerMaand) => {
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const uniqueYears = useMemo(() => {
    if (!berichtenPerMaand) return [];
    return [...new Set(berichtenPerMaand.map(item => item.jaar))];
  }, [berichtenPerMaand]);

  const uniqueMonths = useMemo(() => {
    if (!berichtenPerMaand || selectedYear === 0) return [];
    return berichtenPerMaand
      .filter(item => item.jaar === selectedYear)
      .map(item => item.maand)
      .sort((a, b) => a - b);
  }, [berichtenPerMaand, selectedYear]);

  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
    setSelectedMonth(0); // Reset month when year changes
  }, []);

  return {
    selectedYear,
    selectedMonth,
    uniqueYears,
    uniqueMonths,
    setSelectedYear: handleYearChange,
    setSelectedMonth,
  };
};

/**
 * Hook for filtering subscriptions and interfaces by date range
 */
const useSubscriptionFiltering = (
  abonnementen,
  selectedYear,
  selectedMonth,
  showInactiveAbonnement,
  showInactiveInterface
) => {
  const filteredAbonnementen = useMemo(() => {
    if (!abonnementen) return [];

    let result = [...abonnementen];

    // Filter by active status
    if (!showInactiveAbonnement) {
      result = result.filter(abo => abo.status === 'Actief');
    }

    // Filter by year
    if (selectedYear > 0) {
      result = result.filter(abo => {
        if (!abo.startdatum) return false;
        const startYear = parseInt(abo.startdatum.split('-')[0]);
        const endYear = abo.einddatum ? parseInt(abo.einddatum.split('-')[0]) : Infinity;
        return startYear <= selectedYear && endYear >= selectedYear;
      });

      // Filter interfaces by year
      result = result.map(abo => ({
        ...abo,
        interfaces: abo.interfaces.filter(int => {
          if (!int.startdatum) return false;
          const startYear = parseInt(int.startdatum.split('-')[0]);
          const endYear = int.einddatum ? parseInt(int.einddatum.split('-')[0]) : Infinity;
          return startYear <= selectedYear && endYear >= selectedYear;
        })
      }));
    }

    // Filter by month
    if (selectedMonth > 0 && selectedYear > 0) {
      result = result.filter(abo => {
        if (!abo.startdatum) return false;
        const [startYear, startMonth] = abo.startdatum.split('-').map(Number);
        const startOk = startYear < selectedYear || 
                       (startYear === selectedYear && startMonth <= selectedMonth);
        
        if (!startOk) return false;
        if (!abo.einddatum) return true;
        
        const [endYear, endMonth] = abo.einddatum.split('-').map(Number);
        return endYear > selectedYear || 
               (endYear === selectedYear && endMonth >= selectedMonth);
      });

      // Filter interfaces by month
      result = result.map(abo => ({
        ...abo,
        interfaces: abo.interfaces.filter(int => {
          if (!int.startdatum) return false;
          const [startYear, startMonth] = int.startdatum.split('-').map(Number);
          const startOk = startYear < selectedYear || 
                         (startYear === selectedYear && startMonth <= selectedMonth);
          
          if (!startOk) return false;
          if (!int.einddatum) return true;
          
          const [endYear, endMonth] = int.einddatum.split('-').map(Number);
          return endYear > selectedYear || 
                 (endYear === selectedYear && endMonth >= selectedMonth);
        })
      }));
    }

    // Filter interfaces by active status (if not filtered by date)
    if (selectedYear === 0 && !showInactiveInterface) {
      result = result.map(abo => ({
        ...abo,
        interfaces: abo.interfaces.filter(int => int.status === 'Actief')
      }));
    }

    return result;
  }, [abonnementen, selectedYear, selectedMonth, showInactiveAbonnement, showInactiveInterface]);

  const showFilterButtons = selectedYear === 0 && selectedMonth === 0;

  return { filteredAbonnementen, showFilterButtons };
};

/**
 * Hook for identifying new interfaces (started in last month)
 */
const useNewInterfaces = (abonnementen) => {
  return useMemo(() => {
    if (!abonnementen) return [];

    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    const newInterfaces = [];

    abonnementen.forEach(abonnement => {
      abonnement.interfaces?.forEach(_interface => {
        if (!_interface.startdatum) {
          newInterfaces.push({
            interface: _interface,
            abonnementNaam: abonnement.naam,
          });
          return;
        }

        const interfaceDate = new Date(_interface.startdatum);
        if (interfaceDate >= lastMonth && interfaceDate <= now) {
          newInterfaces.push({
            interface: _interface,
            abonnementNaam: abonnement.naam,
          });
        }
      });
    });

    return newInterfaces;
  }, [abonnementen]);
};

/**
 * Hook for managing graph data selection
 */
const useGraphData = (selectedCustomer) => {
  const { t } = useTranslation();
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [selectedAbonnement, setSelectedAbonnement] = useState(null);

  const graphData = useMemo(() => {
    if (selectedInterface) {
      return selectedInterface.berichtenPerMaand;
    }
    if (selectedAbonnement) {
      return selectedAbonnement.berichtenPerMaand;
    }
    return selectedCustomer?.statistieken?.berichtenPerMaand || [];
  }, [selectedInterface, selectedAbonnement, selectedCustomer]);

  const selectMessage = useMemo(() => {
    if (selectedInterface) {
      return `${t('interface.title')} = ${selectedInterface.omschrijving}`;
    }
    if (selectedAbonnement) {
      return `${t('subscriptions.title')} = ${selectedAbonnement.naam}`;
    }
    return null;
    }, [selectedInterface, selectedAbonnement, t]);

  const handleSelectInterface = useCallback((int) => {
    setSelectedInterface(int);
    setSelectedAbonnement(null);
  }, []);

  const handleSelectAbonnement = useCallback((abo) => {
    setSelectedAbonnement(abo);
    setSelectedInterface(null);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedInterface(null);
    setSelectedAbonnement(null);
  }, []);

  return {
    graphData,
    selectMessage,
    selectedInterface,
    selectedAbonnement,
    handleSelectInterface,
    handleSelectAbonnement,
    handleReset,
  };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const CustomerSelector = ({ customers, selectedIndex, onSelect }) => {
  const { t } = useTranslation();
  const options = useMemo(() => 
    customers.map((customer, i) => ({
      label: customer.naam,
      value: i,
    })),
    [customers]
  );

  return (
    <div className="customer-selector">
      <div className="customer-selector-title">
        {t('dashboard.user')}: {UserService.getUsername()}
      </div>
      <Select
        value={options[selectedIndex]}
        options={options}
        onChange={(option) => onSelect(option.value)}
        className="cursor-pointer"
      />
    </div>
  );
};

const FilterControls = ({ 
  showInactiveAbonnement,
  showInactiveInterface,
  onToggleAbonnement,
  onToggleInterface,
  showButtons,
}) => {
  const { t } = useTranslation();
  if (!showButtons) return null;

  return (
    <div className="filter-controls">
      <div className="filter-checkbox">
        <span>{t('dashboard.showInactiveSubscriptions')}:</span>
        <input
          type="checkbox"
          checked={showInactiveAbonnement}
          onChange={onToggleAbonnement}
          className="cursor-pointer w-4 h-4"
        />
      </div>
      <div className="filter-checkbox">
        <span>{t('dashboard.showInactiveInterfaces')}:</span>
        <input
          type="checkbox"
          checked={showInactiveInterface}
          onChange={onToggleInterface}
          className="cursor-pointer w-4 h-4"
        />
      </div>
    </div>
  );
};

const PeriodSelector = ({ 
  years, 
  months, 
  selectedYear, 
  selectedMonth, 
  onYearChange, 
  onMonthChange 
}) => {
  const { t } = useTranslation();
  const monthLabels = useMemo(() => ([
    t('months.january'),
    t('months.february'),
    t('months.march'),
    t('months.april'),
    t('months.may'),
    t('months.june'),
    t('months.july'),
    t('months.august'),
    t('months.september'),
    t('months.october'),
    t('months.november'),
    t('months.december')
  ]), [t]);

  return (
    <div className="period-selector">
      <div className="period-selector-title">{t('dashboard.period')}:</div>
      <select
        value={selectedYear}
        onChange={(e) => onYearChange(Number(e.target.value))}
        className="period-select"
      >
        <option value={0}>{t('dashboard.year')}</option>
        {years.map(year => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
      <select
        value={selectedMonth}
        onChange={(e) => onMonthChange(Number(e.target.value))}
        className="period-select"
        disabled={selectedYear === 0}
      >
        <option value={0}>{t('dashboard.month')}</option>
        {months.map(month => (
          <option key={month} value={month}>{monthLabels[month - 1] || Months[month - 1]}</option>
        ))}
      </select>
    </div>
  );
};

const SubscriptionTable = ({ 
  abonnementen, 
  year, 
  month,
  showInactiveInterface,
  onSelectInterface, 
  onSelectAbonnement,
  onReset,
}) => {
  const { t } = useTranslation();
  return (
    <div className="subscription-table-container">
      <div className="subscription-table-title">{t('subscriptions.title')}</div>
      <div className="subscription-table-wrapper">
        <table className="subscription-table">
          <thead>
            <tr>
              <th className="w-4/12">{t('subscriptions.title')}</th>
              <th className="w-2/12">{t('subscriptions.startDate')}</th>
              <th className="w-2/12">{t('subscriptions.endDate')}</th>
              <th className="w-1/12">{t('subscriptions.status')}</th>
              <th className="w-1/12">{t('interface.interfaces')}</th>
              <th className="w-1/12">{t('dashboard.messages')}</th>
              <th className="w-1/12"></th>
            </tr>
          </thead>
          {abonnementen.map((abonnement) => (
            <AbonnementCollapsibleKlant
              key={abonnement.id}
              abonnement={abonnement}
              year={year}
              month={month}
              showInactive={showInactiveInterface}
              changeData={onSelectInterface}
              changeDataAbo={onSelectAbonnement}
              resetData={onReset}
              triggerKey={`${year}-${month}`}
            />
          ))}
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DashboardKlant = () => {
  const { t } = useTranslation();
  const { customers, loading: customersLoading, error: customersError } = useCustomers();
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(0);
  const [showInactiveAbonnement, setShowInactiveAbonnement] = useState(false);
  const [showInactiveInterface, setShowInactiveInterface] = useState(false);
  const [showBerichten, setShowBerichten] = useState(true);
  const [berichtenKey, setBerichtenKey] = useState(0);
  
  const berichtenRef = useRef(null);
  const axios = HttpService.getAxiosClient();

  // Get selected customer with updated statistics if needed
  const selectedCustomer = customers[selectedCustomerIndex];
  const { statistics, loading: statsLoading } = useCustomerStatistics(selectedCustomer, axios);
  
  // Merge statistics if fetched
  const currentCustomer = useMemo(() => {
    if (!selectedCustomer) return null;
    if (statistics) {
      return { ...selectedCustomer, ...statistics };
    }
    return selectedCustomer;
  }, [selectedCustomer, statistics]);

  // Period filters
  const periodFilters = usePeriodFilters(currentCustomer?.statistieken?.berichtenPerMaand);

  // Subscription filtering
  const { filteredAbonnementen, showFilterButtons } = useSubscriptionFiltering(
    currentCustomer?.abonnementen,
    periodFilters.selectedYear,
    periodFilters.selectedMonth,
    showInactiveAbonnement,
    showInactiveInterface
  );

  // New interfaces
  const newInterfaces = useNewInterfaces(currentCustomer?.abonnementen);

  // Graph data management
  const {
    graphData,
    selectMessage,
    selectedInterface,
    selectedAbonnement,
    handleSelectInterface,
    handleSelectAbonnement,
    handleReset,
  } = useGraphData(currentCustomer);

  // Auto-show berichten table when interface is selected
  useEffect(() => {
    if (selectedInterface || selectedAbonnement) {
      setShowBerichten(true);
      setTimeout(() => {
        berichtenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedInterface, selectedAbonnement]);

  // Graph options for BerichtenGraph component
  const graphOptions = useMemo(() => ({
    data: graphData,
    uniqueYears: periodFilters.uniqueYears,
    uniqueMonths: periodFilters.uniqueMonths,
    selectedYear: periodFilters.selectedYear,
    selectedMonth: periodFilters.selectedMonth,
  }), [graphData, periodFilters]);

  // Handlers
  const handleCustomerChange = useCallback((index) => {
    setSelectedCustomerIndex(index);
    setShowBerichten(true);
    handleReset();
    periodFilters.setSelectedYear(0);
    periodFilters.setSelectedMonth(0);
  }, [handleReset, periodFilters]);

  const handleShowBerichten = useCallback(() => {
    setShowBerichten(true);
    setTimeout(() => {
      berichtenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleRefreshBerichten = useCallback(() => {
    setBerichtenKey(prev => prev + 1);
  }, []);

  // Loading state
  if (customersLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  // Error state
  if (customersError) {
    return (
      <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-bold text-red-800 mb-2">{t('common.error')}</h3>
        <p className="text-red-600">{customersError}</p>
      </div>
    );
  }

  // No customer selected
  if (!currentCustomer || !currentCustomer.statistieken?.berichtenPerMaand) {
    return (
      <div className="m-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600">{t('common.noDataAvailable')}</p>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
          <h2>{t('dashboard.title')}</h2>
      </div>

      <div className="page-content">
        <div className="dashboard-layout">
          <div className="dashboard-sidebar">
          <CustomerSelector
            customers={customers}
            selectedIndex={selectedCustomerIndex}
            onSelect={handleCustomerChange}
          />

          <FilterControls
            showInactiveAbonnement={showInactiveAbonnement}
            showInactiveInterface={showInactiveInterface}
            onToggleAbonnement={() => setShowInactiveAbonnement(prev => !prev)}
            onToggleInterface={() => setShowInactiveInterface(prev => !prev)}
            showButtons={showFilterButtons}
          />

          <PeriodSelector
            years={periodFilters.uniqueYears}
            months={periodFilters.uniqueMonths}
            selectedYear={periodFilters.selectedYear}
            selectedMonth={periodFilters.selectedMonth}
            onYearChange={periodFilters.setSelectedYear}
            onMonthChange={periodFilters.setSelectedMonth}
          />
        </div>

          <div style={{ gridColumn: "span 3", paddingLeft: "1rem" }}>
            <KlantDetailsSection klant={currentCustomer} />
          </div>
        
        <div className="dashboard-main">
          {newInterfaces.length > 0 && (
            <NieuweInterfacesTabel newInterfaces={newInterfaces} />
          )}

          <SubscriptionTable
            abonnementen={filteredAbonnementen}
            year={periodFilters.selectedYear}
            month={periodFilters.selectedMonth}
            showInactiveInterface={showInactiveInterface}
            onSelectInterface={handleSelectInterface}
            onSelectAbonnement={handleSelectAbonnement}
            onReset={handleReset}
          />
        </div>
      </div>

      <div className="graph-section">
        <div className="graph-container">
          <BerichtenGraph
            graphOptions={graphOptions}
            selectMessage={selectMessage}
            selectedInterface={selectedInterface}
            reset={handleReset}
          />
        </div>

        {/* {showFilterButtons && (
          <LatestMessagesTable messages={currentCustomer.laatsteBerichten} />
        )} */}
      </div>

      <div className="messages-section" ref={berichtenRef}>
        {showBerichten && (
          <BerichtDownloadComponent
            key={berichtenKey}
            interface={selectedInterface}
            abonnementId={selectedAbonnement?.id}
            customerDebiNr={currentCustomer?.debiNr}
            axiosInstance={axios}
          />
        )}
      </div>
      </div>
    </div>
  );
};

export default DashboardKlant;